import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
  Tag,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Tooltip,
  Select
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  ReloadOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface ActivationCode {
  id: number;
  code: string;
  server_id: number | null;
  used_at: string | null;
  created_at: string;
  expires_at: string | null;
  server_name?: string;
  user_name?: string;
}

const ModActivationCodes: React.FC = () => {
  const [codes, setCodes] = useState<ActivationCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCode, setEditingCode] = useState<ActivationCode | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 加载激活码列表
  const loadCodes = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await api.get('/admin/mod-activation-codes', {
        params: { page, pageSize }
      });
      
      setCodes(response.data.codes || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.total || 0
      }));
    } catch (error: any) {
      message.error('加载激活码列表失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 生成激活码
  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'ADD-MOD-';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // 创建/更新激活码
  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD HH:mm:ss') : null
      };

      if (editingCode) {
        await api.put(`/admin/mod-activation-codes/${editingCode.id}`, data);
        message.success('激活码更新成功');
      } else {
        await api.post('/admin/mod-activation-codes', data);
        message.success('激活码创建成功');
      }

      setModalVisible(false);
      setEditingCode(null);
      form.resetFields();
      loadCodes(pagination.current, pagination.pageSize);
    } catch (error: any) {
      message.error('操作失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 删除激活码
  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/mod-activation-codes/${id}`);
      message.success('激活码删除成功');
      loadCodes(pagination.current, pagination.pageSize);
    } catch (error: any) {
      message.error('删除失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 复制激活码
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('激活码已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 批量生成激活码
  const batchGenerate = async (count: number) => {
    try {
      await api.post('/admin/mod-activation-codes/batch', { count });
      message.success(`成功生成 ${count} 个激活码`);
      loadCodes(pagination.current, pagination.pageSize);
    } catch (error: any) {
      message.error('批量生成失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 获取状态标签
  const getStatusTag = (code: ActivationCode) => {
    const now = dayjs();
    const expiresAt = code.expires_at ? dayjs(code.expires_at) : null;
    
    if (code.used_at) {
      return <Tag color="green" icon={<CheckCircleOutlined />}>已使用</Tag>;
    }
    
    if (expiresAt && now.isAfter(expiresAt)) {
      return <Tag color="red" icon={<ExclamationCircleOutlined />}>已过期</Tag>;
    }
    
    return <Tag color="blue" icon={<ClockCircleOutlined />}>未使用</Tag>;
  };

  // 获取状态统计
  const getStats = () => {
    const total = codes.length;
    const used = codes.filter(code => code.used_at).length;
    const expired = codes.filter(code => {
      if (code.used_at) return false;
      const expiresAt = code.expires_at ? dayjs(code.expires_at) : null;
      return expiresAt && dayjs().isAfter(expiresAt);
    }).length;
    const available = total - used - expired;
    
    return { total, used, expired, available };
  };

  const stats = getStats();

  useEffect(() => {
    loadCodes();
  }, []);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '激活码',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (code: string) => (
        <Space>
          <Text code>{code}</Text>
          <Tooltip title="复制">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyCode(code)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (record: ActivationCode) => getStatusTag(record),
    },
    {
      title: '使用服务器',
      dataIndex: 'server_name',
      key: 'server_name',
      width: 150,
      render: (serverName: string, record: ActivationCode) => {
        if (record.used_at && serverName) {
          return (
            <Space direction="vertical" size={0}>
              <Text>{serverName}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                用户: {record.user_name}     服务器id:{record.server_id}
              </Text>
            </Space>
          );
        }
        return <Text type="secondary">未使用</Text>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 150,
      render: (date: string | null) => {
        if (!date) return <Text type="secondary">永不过期</Text>;
        const isExpired = dayjs().isAfter(dayjs(date));
        return (
          <Text type={isExpired ? 'danger' : 'secondary'}>
            {dayjs(date).format('YYYY-MM-DD HH:mm')}
          </Text>
        );
      },
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 150,
      render: (date: string | null) => {
        if (!date) return <Text type="secondary">未使用</Text>;
        return dayjs(date).format('YYYY-MM-DD HH:mm');
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (record: ActivationCode) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingCode(record);
                form.setFieldsValue({
                  code: record.code,
                  expires_at: record.expires_at ? dayjs(record.expires_at) : null
                });
                setModalVisible(true);
              }}
              disabled={!!record.used_at}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定要删除这个激活码吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3}>
            <KeyOutlined style={{ marginRight: 8 }} />
            MOD激活码管理
          </Title>
          <Text type="secondary">
            管理ARK生存进化MOD功能的激活码，支持创建、编辑、删除和批量生成
          </Text>
        </div>

        {/* 统计信息 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="总激活码"
                value={stats.total}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已使用"
                value={stats.used}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="可用"
                value={stats.available}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已过期"
                value={stats.expired}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 操作按钮 */}
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingCode(null);
              form.resetFields();
              form.setFieldsValue({ code: generateCode() });
              setModalVisible(true);
            }}
          >
            创建激活码
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadCodes(pagination.current, pagination.pageSize)}
          >
            刷新
          </Button>
          <Button
            onClick={() => {
              Modal.confirm({
                title: '批量生成激活码',
                content: (
                  <div>
                    <p>请输入要生成的激活码数量：</p>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      placeholder="1-100"
                      id="batchCount"
                    />
                  </div>
                ),
                onOk: () => {
                  const count = parseInt((document.getElementById('batchCount') as HTMLInputElement)?.value || '1');
                  if (count >= 1 && count <= 100) {
                    batchGenerate(count);
                  } else {
                    message.error('请输入1-100之间的数字');
                  }
                }
              });
            }}
          >
            批量生成
          </Button>
        </Space>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={codes}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              loadCodes(page, pageSize || 10);
            }
          }}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingCode ? '编辑激活码' : '创建激活码'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCode(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="code"
            label="激活码"
            rules={[{ required: true, message: '请输入激活码' }]}
          >
            <Input
              placeholder="输入激活码或留空自动生成"
              addonAfter={
                <Button
                  type="text"
                  onClick={() => form.setFieldsValue({ code: generateCode() })}
                >
                  生成
                </Button>
              }
            />
          </Form.Item>

          <Form.Item
            name="expires_at"
            label="过期时间"
            help="留空表示永不过期"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              placeholder="选择过期时间"
            />
          </Form.Item>

          {!editingCode && (
            <Alert
              message="提示"
              description="激活码一旦创建将无法修改，请确保信息正确。"
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ModActivationCodes;

